// src/pages/Legal.jsx
import React, { useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useApp } from '../context/AppContext'

const LAST_UPDATED = 'June 2026'
const CONTACT_EMAIL = 'tecfin@kempu.in'
const APP_NAME = 'TecFin'
const APP_URL = 'https://tecfin.kempu.in'

function Section({ id, title, children }) {
  return (
    <section id={id} style={{ marginBottom: 40 }}>
      <h2 style={{
        fontSize: 18, fontWeight: 600, color: 'var(--gray-900)',
        marginBottom: 14, paddingBottom: 10,
        borderBottom: '1px solid var(--border)',
      }}>
        {title}
      </h2>
      {children}
    </section>
  )
}

function Para({ children }) {
  return <p style={{ fontSize: 14, color: 'var(--gray-700)', lineHeight: 1.75, marginBottom: 12 }}>{children}</p>
}

function SubHead({ children }) {
  return <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-900)', margin: '18px 0 6px' }}>{children}</h3>
}

function Ul({ items }) {
  return (
    <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
      {items.map((item, i) => (
        <li key={i} style={{ fontSize: 14, color: 'var(--gray-700)', lineHeight: 1.75, marginBottom: 4 }}>{item}</li>
      ))}
    </ul>
  )
}

export default function Legal() {
  const { hash } = useLocation()
  const { currentUser } = useApp()
  const backTo = currentUser ? '/transactions' : '/login'

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''))
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
    } else {
      window.scrollTo(0, 0)
    }
  }, [hash])

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '8px 0 40px' }}>

      {/* Page header */}
      <div style={{ marginBottom: 32, paddingBottom: 20, borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <Link to={backTo} style={{ color: 'var(--gray-600)', textDecoration: 'none', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
            <i className="ti ti-arrow-left" aria-hidden="true" /> Back
          </Link>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--gray-900)', marginBottom: 6 }}>
          Legal information
        </h1>
        <p style={{ fontSize: 13, color: 'var(--gray-600)' }}>
          Last updated: {LAST_UPDATED} &nbsp;·&nbsp; Questions? &nbsp;
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--brand-600)', fontWeight: 500 }}>{CONTACT_EMAIL}</a>
        </p>

        {/* Jump links */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          {[
            { href: '#disclaimer', label: 'Disclaimer' },
            { href: '#privacy',    label: 'Privacy policy' },
            { href: '#terms',      label: 'Terms & conditions' },
          ].map(l => (
            <a
              key={l.href}
              href={l.href}
              style={{
                fontSize: 12, padding: '5px 12px', borderRadius: 20,
                border: '1px solid var(--border)', background: 'var(--surface-alt)',
                color: 'var(--brand-600)', textDecoration: 'none', fontWeight: 500,
              }}
            >
              {l.label}
            </a>
          ))}
        </div>
      </div>

      {/* ── DISCLAIMER ────────────────────────────────── */}
      <Section id="disclaimer" title="Disclaimer">
        <Para>
          {APP_NAME} is a personal finance tracking tool provided for informational and
          organisational purposes only. The information, data, and reports generated by
          {APP_NAME} do not constitute financial, investment, tax, legal, or accounting advice.
        </Para>
        <SubHead>No professional advice</SubHead>
        <Para>
          Nothing in {APP_NAME} should be construed as professional financial advice. You
          should always consult a qualified financial advisor, accountant, or tax professional
          before making any financial decisions based on the data or reports generated by this
          application.
        </Para>
        <SubHead>Accuracy of data</SubHead>
        <Para>
          {APP_NAME} relies entirely on data you enter. We do not independently verify,
          validate, or audit any figures, balances, or transaction records. The accuracy of
          your financial summary depends wholly on the accuracy of the data you input.
        </Para>
        <SubHead>Limitation of liability</SubHead>
        <Para>
          To the fullest extent permitted by applicable law, {APP_NAME} and its operators
          shall not be liable for any direct, indirect, incidental, consequential, or
          special damages arising out of or relating to your use of this application,
          including but not limited to financial loss, data loss, or loss of business
          opportunity.
        </Para>
        <SubHead>Third-party services</SubHead>
        <Para>
          {APP_NAME} uses third-party services including Supabase (database and
          authentication) and Razorpay (payments). We are not responsible for the
          availability, performance, or actions of these third-party services. Their
          respective terms of service and privacy policies apply independently.
        </Para>
      </Section>

      {/* ── PRIVACY POLICY ────────────────────────────── */}
      <Section id="privacy" title="Privacy policy">
        <Para>
          This Privacy Policy explains what information {APP_NAME} collects, how it is
          used, and how it is protected. By using {APP_NAME}, you agree to the collection
          and use of information in accordance with this policy.
        </Para>

        <SubHead>What we collect</SubHead>
        <Ul items={[
          'Account information: your name and email address, provided at registration.',
          'Financial data: transaction records, account balances, and categories you enter manually.',
          'Payment information: plan type and payment status. We do not store raw card numbers or bank details — payments are processed by Razorpay under their own PCI-DSS compliance.',
          'Usage data: page views and feature usage, collected in aggregate to improve the app. No personally identifiable analytics are shared with third parties.',
        ]} />

        <SubHead>How we use your data</SubHead>
        <Ul items={[
          'To provide, maintain, and improve the TecFin service.',
          'To authenticate you and keep your account secure.',
          'To process subscription payments and manage your plan.',
          'To respond to your support requests or enquiries.',
          'We never sell your personal data to third parties.',
        ]} />

        <SubHead>Data storage and security</SubHead>
        <Para>
          Your data is stored in a Supabase-managed PostgreSQL database with row-level
          security enabled, meaning each user can only access their own records. Data is
          encrypted in transit using TLS. Supabase infrastructure is hosted on AWS and
          subject to their security standards.
        </Para>

        <SubHead>Data retention</SubHead>
        <Para>
          Your data is retained for as long as your account is active. If you wish to
          delete your account and all associated data, contact us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--brand-600)' }}>{CONTACT_EMAIL}</a>{' '}
          and we will process your request within 30 days.
        </Para>

        <SubHead>Cookies</SubHead>
        <Para>
          {APP_NAME} uses session tokens (stored in your browser's local storage) to keep
          you logged in. We do not use advertising or tracking cookies. No third-party
          cookies are set by our application.
        </Para>

        <SubHead>Third-party processors</SubHead>
        <Ul items={[
          'Supabase (supabase.com) — authentication and database storage.',
          'Razorpay (razorpay.com) — payment processing for paid plans.',
          'Hostinger — web hosting and file delivery.',
        ]} />

        <SubHead>Your rights</SubHead>
        <Para>
          Depending on your location, you may have rights under applicable data protection
          laws (including GDPR, CCPA, and India's DPDP Act 2023) to access, correct,
          export, or delete your personal data. To exercise any of these rights, contact
          us at <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--brand-600)' }}>{CONTACT_EMAIL}</a>.
        </Para>

        <SubHead>Changes to this policy</SubHead>
        <Para>
          We may update this Privacy Policy from time to time. We will notify users of
          material changes by updating the "Last updated" date at the top of this page.
          Continued use of {APP_NAME} after changes constitutes acceptance of the updated
          policy.
        </Para>
      </Section>

      {/* ── TERMS & CONDITIONS ────────────────────────── */}
      <Section id="terms" title="Terms &amp; conditions">
        <Para>
          By accessing or using {APP_NAME} at{' '}
          <a href={APP_URL} style={{ color: 'var(--brand-600)' }}>{APP_URL}</a>{' '}
          you agree to be bound by these Terms and Conditions. If you do not agree to
          any part of these terms, you may not use the application.
        </Para>

        <SubHead>Eligibility</SubHead>
        <Para>
          You must be at least 18 years old to use {APP_NAME}. By using this application,
          you represent that you meet this requirement and that all information you provide
          is accurate and complete.
        </Para>

        <SubHead>Acceptable use</SubHead>
        <Para>You agree not to:</Para>
        <Ul items={[
          'Use the application for any unlawful purpose or in violation of any applicable laws.',
          'Attempt to gain unauthorised access to other users\' data or the underlying systems.',
          'Reverse engineer, decompile, or attempt to extract the source code of the application.',
          'Use automated scripts or bots to interact with the application.',
          'Share your account credentials with others.',
        ]} />

        <SubHead>Plans and payments</SubHead>
        <Para>
          {APP_NAME} offers a Free plan and paid plans (Pro, Premium, Enterprise). Paid
          plan fees are as displayed on the Pricing page at the time of purchase. All
          prices are inclusive of applicable taxes unless stated otherwise.
        </Para>
        <Para>
          One-time plan payments (Pro) are non-refundable once activated. Monthly
          subscriptions (Premium) may be cancelled at any time; access continues until the
          end of the current billing period. No partial refunds are issued for unused
          subscription time.
        </Para>
        <Para>
          For Enterprise plan enquiries, contact{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--brand-600)' }}>{CONTACT_EMAIL}</a>.
        </Para>

        <SubHead>Intellectual property</SubHead>
        <Para>
          All content, design, code, and materials comprising {APP_NAME} are the property
          of {APP_NAME} and its operators. You are granted a limited, non-exclusive,
          non-transferable licence to use the application for its intended purpose. No
          rights are transferred to you beyond this licence.
        </Para>

        <SubHead>Data ownership</SubHead>
        <Para>
          You retain full ownership of all financial data you enter into {APP_NAME}. We
          claim no rights over your data. You are responsible for maintaining your own
          backups of important financial records.
        </Para>

        <SubHead>Service availability</SubHead>
        <Para>
          We aim to provide reliable service but do not guarantee uninterrupted or
          error-free access to {APP_NAME}. We reserve the right to suspend, modify, or
          discontinue the service at any time, with reasonable notice where practicable.
        </Para>

        <SubHead>Termination</SubHead>
        <Para>
          We reserve the right to suspend or terminate your account if you violate these
          Terms. You may close your account at any time by contacting us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--brand-600)' }}>{CONTACT_EMAIL}</a>.
        </Para>

        <SubHead>Governing law</SubHead>
        <Para>
          These Terms are governed by and construed in accordance with the laws of India.
          Any disputes arising under these Terms shall be subject to the exclusive
          jurisdiction of the courts of India, without prejudice to your statutory rights
          in your country of residence.
        </Para>

        <SubHead>Changes to these terms</SubHead>
        <Para>
          We may revise these Terms from time to time. Continued use of {APP_NAME} after
          any revision constitutes your acceptance of the updated Terms. We will notify
          users of significant changes by updating the date at the top of this page.
        </Para>

        <SubHead>Contact</SubHead>
        <Para>
          For any questions about these Terms, Privacy Policy, or the Disclaimer, please
          contact us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--brand-600)', fontWeight: 500 }}>{CONTACT_EMAIL}</a>.
        </Para>
      </Section>
    </div>
  )
}
